// src/ui/screens/deliveries.ts
// BALL BY BALL — THE CORE LOOP. One tap per delivery, fifty to a hundred times, outdoors, one-handed,
// possibly with no signal at all.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHAT THIS SCREEN IS
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The backend has had `sports_practice_deliveries` — "THE ATOMIC UNIT. One row = one ball" — live in
// production since 26 Aug 2026, and until this file existed no client could write one. Everything
// downstream (the focus lane's six-ball block summaries, workload counts, the parent report's
// `balls_recorded`, every metric observation, every coach review) hangs off a delivery row. So this
// is the screen the product is actually for.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// THE VOCABULARY IS TRANSCRIBED, NOT INVENTED
// ══════════════════════════════════════════════════════════════════════════════════════════════
// Every literal below was copied from two places that must agree, and they do:
//
//   · dcs-sports-backend/sb/src/practice/delivery-service.ts lines 115-125
//       BALL_SOURCES · ATHLETE_ROLES · OUTCOME_CLASSES · OUTCOME_SOURCES · QUALITY_STATES ·
//       QUALITY_REASON_CODES
//   · dcs-sports-backend/sb/migrations/041_practice_lab_schema.sql lines 690-711
//       the CHECK constraints on `sports_practice_deliveries` that carry the same six lists
//
// A term this app made up would be rejected by a database CHECK, but only after the coach had
// already tapped it fifty times — the ball would look recorded on the phone and be a 400 on the
// wire. So there is no "other", no free-text outcome, and no local synonym.
//
// TWO SERVER RULES ARE MADE UNREPRESENTABLE HERE rather than merely avoided:
//
//   1. `quality_state DEGRADED|UNUSABLE requires a quality_reason_code` (041 line 726,
//      delivery-service.ts line 1197). `QualityChoice` is a discriminated union in which the two
//      states that need a reason CARRY one, so the invalid pairing cannot be constructed.
//   2. `a USABLE delivery must name a detector OR be tagged by a human` (041 line 730,
//      delivery-service.ts line 1204). This client has no detector and will never claim one —
//      `detector_version` is never sent, and `outcome_source` is always ATHLETE_TAGGED or
//      COACH_TAGGED, because a person is literally standing there tapping. AI_INFERRED is in the
//      backend's vocabulary and is deliberately NOT offered: nothing on this phone infers anything.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// BLOCKS ARE SIX BALLS, AND THE ARITHMETIC IS THE FOCUS LANE'S OWN
// ══════════════════════════════════════════════════════════════════════════════════════════════
// `focus-service.ts` exports `BLOCK_SIZE = 6` and `blockOf(ball) = floor((ball-1)/6)+1`, and it
// posts a block summary "every six balls". `blockOf()` below is that function, character for
// character, over the delivery SEQUENCE. If the two ever disagree, a coach's "six balls done"
// arrives on the wrong ball, so it is restated here rather than approximated.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// OFFLINE-FIRST, AND THE IDEMPOTENCY PROBLEM THIS LANE ACTUALLY HAS
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The session lane has `(client_device_id, client_session_uuid)` and the upload lane has
// `client_upload_uuid`; both let the SERVER recognise a replay and answer 200 `replayed: true`.
//
// The delivery lane has no such key. Its only uniqueness is the database constraint
// `unique (session_id, sequence)` (041 line 721), and `ingestDelivery` turns a violation into
// `409 delivery N already recorded for this session`. That 409 is ambiguous in exactly the case
// that matters: after a lost acknowledgement, "the row you are re-sending is already there" and
// "somebody else took ball 12" are the same response.
//
// Resolving it by assuming the first would be a client that reports a ball as recorded on someone
// else's evidence. Resolving it by assuming the second would strand a ball that IS recorded, and
// then double-count it when a coach renumbered. So this screen does neither and PROVES which it is:
//
//   · every ball carries a device-minted `client_idempotency` stamp inside `events_json` — a uuid,
//     the device id, and the device's own clock reading at the moment of the tap;
//   · on a 409 the client GETs the session's deliveries and looks at the row holding that sequence;
//   · the stamp matches       → this is OUR ball, already written. State becomes `recorded`, and
//                               `replay_proved` records that nothing was written a second time.
//   · the stamp differs/absent → a genuine collision. State becomes `collision`, the server's own
//                               409 sentence is shown, and the ball is NOT counted as recorded.
//   · the GET itself refuses  → nothing is decided. The ball stays `refused` with the 409 sentence.
//                               An unresolved ambiguity is left visible rather than guessed.
//
// This is a client-side workaround for a missing server-side key, and it is written down as one:
// see the note on `IDEMPOTENCY_STAMP_KEY`. The real fix belongs in the backend.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// WHY THE BALL QUEUE IS ITS OWN STORE AND NOT A THIRD `Job` KIND
// ══════════════════════════════════════════════════════════════════════════════════════════════
// `queue/queue.ts` is the right home for this, and that is where it should end up. It is not there
// today for a mechanical reason: `Job` is a closed union of `session | upload`, and `drain()` ends
// `if (job.kind === 'session') … else await runUploadJob(job)` — so a delivery job put in the
// `jobs` store would be run as an UPLOAD, with no file. Adding the third kind means editing the
// shared queue while five other screens are being built against it in parallel.
//
// So the ball queue reuses the queue's INFRASTRUCTURE and its SEMANTICS, in this file, and reuses
// nothing by copy: the same `store/idb.ts` database, the same `device/ids.ts` device id, the same
// `api/refusal.ts` classification, the same `needsOperator()` held-vs-failed rule, the same
// crash-recovery reset, the same serial reentrancy-guarded drain. The integration owner should fold
// `BallJob` into `queue/queue.ts` as `kind: 'delivery'` once the lanes have merged; the states and
// transitions here were written to make that a move, not a rewrite.
//
// One real difference, and it is a property not an accident: the whole ball queue is ONE IndexedDB
// document, updated through a single `idbUpdate` read-modify-write. Sequence allocation and the
// append therefore happen inside one transaction, so a double-tap on a laggy phone cannot hand two
// balls the same sequence.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
// QUEUED IS NOT RECORDED
// ══════════════════════════════════════════════════════════════════════════════════════════════
// The counts on this screen never merge the two populations. "Recorded on the server" counts balls
// the server has acknowledged; "waiting on this phone" counts balls that exist only here. A single
// number would be the exact lie this product is built not to tell — and it is the number a coach
// would quote to a parent.

import { el, empty, field, mount, section, why } from '../dom';
import { refusalView } from '../refusal-view';
import { getState } from '../state';
import { PRACTICE_NAMESPACE as NS } from '../../config';
import { isRefusal, request, type ApiResult } from '../../api/client';
import { classify, needsOperator, unreachable, type Refusal, type RefusalKind } from '../../api/refusal';
import { STORE, idbGet, idbUpdate } from '../../store/idb';
import { deviceId, newSessionUuid } from '../../device/ids';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 1 · THE VOCABULARY — transcribed from delivery-service.ts 115-125 and migration 041 690-711
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

export const BALL_SOURCES = [
  'LIVE_BOWLER', 'BOWLING_MACHINE', 'SIDEARM', 'THROWDOWN', 'SELF_FEED', 'UNKNOWN',
] as const;
export type BallSource = (typeof BALL_SOURCES)[number];

export const ATHLETE_ROLES = ['BOWLING', 'BATTING', 'FIELDING'] as const;
export type AthleteRole = (typeof ATHLETE_ROLES)[number];

export const OUTCOME_CLASSES = [
  'DOT', 'RUNS', 'BOUNDARY', 'WICKET', 'MISS', 'EDGE', 'LEAVE', 'NO_CONTACT', 'PLAY_AND_MISS',
  'NOT_SCORED',
] as const;
export type OutcomeClass = (typeof OUTCOME_CLASSES)[number];

export const OUTCOME_SOURCES = ['ATHLETE_TAGGED', 'COACH_TAGGED', 'AI_INFERRED', 'UNSCORED'] as const;
export type OutcomeSource = (typeof OUTCOME_SOURCES)[number];

/**
 * The two a human tapping a phone may legitimately claim.
 *
 * AI_INFERRED is a real value in the backend's enum and is excluded on purpose: no model runs on
 * this device, so a ball tagged AI_INFERRED from here would be an unattributed claim. UNSCORED is
 * excluded from the *selector* because every ball this screen sends WAS tagged by the person
 * holding the phone — that is what the tap is. (`outcome_class: 'NOT_SCORED'` is how "the ball
 * happened and nobody judged it" is expressed; see the note on the outcome selector.)
 */
export const HUMAN_OUTCOME_SOURCES = ['ATHLETE_TAGGED', 'COACH_TAGGED'] as const;
export type HumanOutcomeSource = (typeof HUMAN_OUTCOME_SOURCES)[number];

export const QUALITY_STATES = ['PENDING', 'USABLE', 'DEGRADED', 'UNUSABLE'] as const;
export type QualityState = (typeof QUALITY_STATES)[number];

export const QUALITY_REASON_CODES = [
  'OCCLUSION', 'MOTION_BLUR', 'BALL_LOST', 'LOW_LIGHT', 'CAMERA_MOVED', 'OUT_OF_FRAME',
  'FPS_BELOW_MINIMUM', 'CLIP_TRUNCATED', 'NOT_A_DELIVERY', 'OTHER',
] as const;
export type QualityReasonCode = (typeof QUALITY_REASON_CODES)[number];

/**
 * THE PAIRING THE DATABASE REQUIRES, EXPRESSED SO THE WRONG ONE CANNOT BE BUILT.
 *
 * 041 line 726: `quality_state in ('PENDING','USABLE') or quality_reason_code is not null`.
 * A union with the reason attached to exactly the two states that need one means the invalid
 * combination is a type error here rather than a 400 at the ground.
 */
export type QualityChoice =
  | { readonly state: 'PENDING'; readonly reason: null }
  | { readonly state: 'USABLE'; readonly reason: null }
  | { readonly state: 'DEGRADED'; readonly reason: QualityReasonCode }
  | { readonly state: 'UNUSABLE'; readonly reason: QualityReasonCode };

/** focus-service.ts `BLOCK_SIZE`. Restated, not approximated — see the header block. */
export const BLOCK_SIZE = 6;

/** focus-service.ts `blockOf`, over the delivery sequence. 1-based block for a 1-based ball. */
export function blockOf(sequence: number): number {
  return Math.floor((Math.max(1, sequence) - 1) / BLOCK_SIZE) + 1;
}

/** Position within the six-ball block, 1-based. */
export function ballInBlock(sequence: number): number {
  return ((Math.max(1, sequence) - 1) % BLOCK_SIZE) + 1;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 2 · THE FIVE ROUTES — transcribed from routes/deliveries.ts, not guessed
 *
 * Nothing in a body carries identity: the session is a PATH parameter and the actor is the verified
 * bearer principal. `athlete_id`, `session_id`, `review_state` and the rest of
 * delivery-service.ts's CLIENT_FORBIDDEN_FIELDS are absent from `DeliveryBody` by construction, so
 * a careless edit here cannot send one.
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** The device's own stamp, carried in `events_json`. See the header block for why it exists. */
export interface IdempotencyStamp {
  /** Minted once, at enqueue, and never regenerated — the same rule device/ids.ts states. */
  readonly delivery_uuid: string;
  readonly device_id: string;
  /**
   * THE DEVICE'S CLOCK AT THE MOMENT OF THE TAP.
   *
   * `sports_practice_deliveries.detected_at` defaults to `now()` on the SERVER and `DeliveryInput`
   * has no field to override it — so a ball tapped at 10:00 with no signal and replayed at 11:30 is
   * stored as having happened at 11:30. That is a real fidelity loss for exactly the offline case
   * this product is built around. It cannot be fixed from this lane; recording the device's own
   * reading here means the truth is at least preserved next to the row rather than destroyed.
   */
  readonly recorded_at_device: string;
}

/**
 * The key `events_json` is namespaced under.
 *
 * `events_json` is documented in 041 as "detected events (ms offsets into the clip)", so a client
 * idempotency stamp is a guest in it. It is namespaced and named rather than smuggled in, because
 * the honest description of this is "a workaround for a key the delivery lane does not have".
 * THE REAL FIX IS A SERVER-SIDE ONE: `unique (session_id, client_delivery_uuid)` alongside the
 * existing `unique (session_id, sequence)`, and a `replayed: true` answer like the session lane's.
 * Until that exists this stamp is the only thing standing between a lost acknowledgement and either
 * a double-counted or a silently-dropped ball.
 */
export const IDEMPOTENCY_STAMP_KEY = 'client_idempotency';

/** Exactly the fields this client sends. Nothing here is an identity or an authority claim. */
export interface DeliveryBody {
  readonly sequence: number;
  readonly spell_number?: number;
  readonly athlete_role: AthleteRole;
  readonly ball_source: BallSource;
  readonly outcome_class: OutcomeClass;
  readonly outcome_source: HumanOutcomeSource;
  readonly quality_state: QualityState;
  readonly quality_reason_code?: QualityReasonCode;
  readonly events_json: Record<string, unknown>;
}

/** `sports_practice_deliveries`, as the service returns it (`select()` on the whole row). */
export interface DeliveryRow {
  id: string;
  session_id: string;
  athlete_id: string;
  sequence: number;
  spell_number: number | null;
  ball_source: string;
  athlete_role: string;
  outcome_class: string;
  outcome_source: string;
  quality_state: string;
  quality_reason_code: string | null;
  review_state: string;
  detected_at: string | null;
  events_json?: Record<string, unknown> | null;
  [k: string]: unknown;
}

export interface DeliveryIngestResult {
  delivery: DeliveryRow;
  observations: readonly ObservationWire[];
  /** The OBSERVED capture conditions, read from the session rows. No body field contributes. */
  capture: Record<string, unknown>;
  /** Fields the client sent that the gate owns. Named back rather than silently dropped. */
  ignored_client_fields: readonly string[];
  measured_count: number;
  refused_count: number;
  disclaimer: string;
}

export interface DeliveryListResult {
  session_id: string;
  athlete_id: string;
  deliveries: readonly DeliveryRow[];
  capture: Record<string, unknown>;
  disclaimer: string;
}

export interface ObservationRow {
  id?: string;
  metric_id: string;
  observation_state: 'MEASURED' | 'REFUSED';
  refusal_reason_code: string | null;
  measurement_class: string | null;
  review_state: string | null;
  unit: string | null;
  ai_value_num?: number | null;
  [k: string]: unknown;
}

export interface ObservationWire {
  metric_id: string;
  observation_state: 'MEASURED' | 'REFUSED' | 'NOT_PERSISTED';
  persisted: boolean;
  row: ObservationRow | null;
  gate: unknown;
  note: string | null;
  ignored_client_fields: readonly string[];
}

export interface ObservationListResult {
  delivery_id: string | null;
  session_id: string;
  observations: readonly ObservationRow[];
  measured_count: number;
  refused_count: number;
  disclaimer: string;
  _note?: string;
}

export interface ObservationIngestResult {
  observations: readonly ObservationWire[];
  measured_count: number;
  refused_count: number;
  [k: string]: unknown;
}

/** A metric offered to the registry gate. The gate decides class, unit and whether it may exist. */
export interface MetricInput {
  metric_id: string;
  value_num?: number | null;
  value_low?: number | null;
  value_high?: number | null;
  value_category?: string | null;
  uncertainty_num?: number | null;
  uncertainty_kind?: string | null;
  confidence?: number | null;
  model_version?: string | null;
  output_json?: Record<string, unknown> | null;
}

/** POST /v1/practice/sessions/:sessionId/deliveries — 201. Record one ball. */
export function recordDelivery(
  sessionId: string,
  body: DeliveryBody,
): Promise<ApiResult<DeliveryIngestResult>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}/deliveries`, { method: 'POST', body });
}

/** GET /v1/practice/sessions/:sessionId/deliveries — the session's balls, in sequence. */
export function listDeliveries(sessionId: string): Promise<ApiResult<DeliveryListResult>> {
  return request(`${NS}/sessions/${encodeURIComponent(sessionId)}/deliveries`);
}

/**
 * GET /v1/practice/deliveries/:deliveryId/observations — MEASURED and REFUSED together, always.
 *
 * Both are rendered. A REFUSED row is why a number is absent; hiding it turns "we could not measure
 * that" into "nothing happened", which is the backend's own sentence for why it returns them.
 */
export function listDeliveryObservations(deliveryId: string): Promise<ApiResult<ObservationListResult>> {
  return request(`${NS}/deliveries/${encodeURIComponent(deliveryId)}/observations`);
}

/**
 * POST /v1/practice/deliveries/:deliveryId/observations — DECLARED, AND NOT CALLED BY THIS SCREEN.
 *
 * That is a deliberate refusal, not an omission. An observation is a MEASUREMENT. This device runs
 * no model, holds no calibration and reads no sensor — the whole reason `capture.ts` refuses to open
 * a browser camera is that nothing here can be trusted to produce a frame rate, let alone a release
 * speed. Any number this screen posted would be one it made up, and a fabricated metric is precisely
 * what `delivery-service.ts` and the Metric Card registry exist to make impossible.
 *
 * It is declared so the shape is on record for the lane that will have a real measurement source
 * (the analysis pipeline, or a paired sensor), and left uncalled — the same posture `api/practice.ts`
 * takes with `scoutEvidence`.
 */
export function recordDeliveryObservations(
  deliveryId: string,
  metrics: readonly MetricInput[],
): Promise<ApiResult<ObservationIngestResult>> {
  return request(`${NS}/deliveries/${encodeURIComponent(deliveryId)}/observations`, {
    method: 'POST',
    body: { metrics },
  });
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 3 · THE BALL QUEUE — IndexedDB-backed, survives a reload and a force-quit
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Mirrors `queue/queue.ts`'s `JobState`, with the one state the delivery lane needs that the upload
 * lane does not.
 *
 *     queued ──▶ sending ──▶ recorded
 *        ▲          │
 *        └──────────┼──▶ refused    (retrying could plausibly change the answer)
 *                   ├──▶ held       (a person with deployment access must act; the drain stops)
 *                   └──▶ collision  (the sequence belongs to a row that is NOT this ball)
 *
 * `collision` exists because a 409 on `unique (session_id, sequence)` that is provably not ours is
 * neither transient nor an operator problem — it is a numbering conflict only a person can settle,
 * and spinning on it or hiding it are both wrong.
 */
export type BallState = 'queued' | 'sending' | 'recorded' | 'refused' | 'held' | 'collision';

export interface BallJob {
  id: string;
  session_id: string;
  athlete_id: string;
  sequence: number;
  state: BallState;
  attempts: number;
  created_at: number;
  updated_at: number;
  /** Minted ONCE at enqueue and stored with the ball. Never regenerated — see device/ids.ts. */
  client_delivery_uuid: string;
  client_device_id: string;
  /** Built at enqueue and sent byte-identical on every attempt. */
  body: DeliveryBody;
  /** The SERVER's own sentence for the last refusal. Never one this client invented. */
  last_error: string | null;
  last_error_kind: RefusalKind | null;
  last_error_detail: unknown;
  /** The row id the server returned, once it has. */
  server_delivery_id: string | null;
  /** True when a 409 was resolved by finding THIS ball's stamp already on the server's row. */
  replay_proved: boolean | null;
}

interface QueueDoc {
  version: 1;
  balls: BallJob[];
  /**
   * The highest sequence the SERVER has confirmed, per session, learned from the GET.
   *
   * Without it a phone that joins a session another device has been recording would start at ball 1
   * and 409 its way through the first over. With it, allocation starts above what the server holds.
   */
  high_water: Record<string, number>;
}

/** One document, one transaction. `STORE.kv` has no keyPath, so the key is explicit. */
export const BALL_QUEUE_KEY = 'practice_delivery_queue';

const EMPTY_DOC: QueueDoc = { version: 1, balls: [], high_water: {} };

function normalise(cur: QueueDoc | undefined): QueueDoc {
  if (!cur || !Array.isArray(cur.balls)) return { ...EMPTY_DOC, balls: [], high_water: {} };
  return { version: 1, balls: cur.balls, high_water: cur.high_water ?? {} };
}

async function readDoc(): Promise<QueueDoc> {
  return normalise(await idbGet<QueueDoc>(STORE.kv, BALL_QUEUE_KEY));
}

/** Read-modify-write inside ONE transaction — the property sequence allocation depends on. */
function updateDoc<T>(fn: (doc: QueueDoc) => { doc: QueueDoc; out: T }): Promise<T> {
  let captured!: T;
  return idbUpdate<QueueDoc>(STORE.kv, BALL_QUEUE_KEY, (cur) => {
    const { doc, out } = fn(normalise(cur));
    captured = out;
    return doc;
  }).then(() => captured);
}

export async function listBalls(sessionId?: string): Promise<BallJob[]> {
  const doc = await readDoc();
  const balls = sessionId ? doc.balls.filter((b) => b.session_id === sessionId) : doc.balls;
  return [...balls].sort((a, b) => a.sequence - b.sequence);
}

/** Test-only, and the "start a fresh net" control. */
export async function clearBalls(): Promise<void> {
  await updateDoc(() => ({ doc: { ...EMPTY_DOC, balls: [], high_water: {} }, out: undefined }));
}

/** Record what the server holds, so allocation never starts below it. */
export async function noteServerSequences(sessionId: string, sequences: readonly number[]): Promise<void> {
  const highest = sequences.reduce((m, s) => (Number.isFinite(s) && s > m ? s : m), 0);
  await updateDoc((doc) => ({
    doc: { ...doc, high_water: { ...doc.high_water, [sessionId]: Math.max(doc.high_water[sessionId] ?? 0, highest) } },
    out: undefined,
  }));
}

function nextSequenceIn(doc: QueueDoc, sessionId: string): number {
  const local = doc.balls
    .filter((b) => b.session_id === sessionId && b.state !== 'collision')
    .reduce((m, b) => Math.max(m, b.sequence), 0);
  return Math.max(local, doc.high_water[sessionId] ?? 0) + 1;
}

/** What the big button will send next. Read-only; allocation happens inside the enqueue. */
export async function nextSequence(sessionId: string): Promise<number> {
  return nextSequenceIn(await readDoc(), sessionId);
}

export interface BallInput {
  sessionId: string;
  athleteId: string;
  athleteRole: AthleteRole;
  ballSource: BallSource;
  outcomeClass: OutcomeClass;
  outcomeSource: HumanOutcomeSource;
  quality: QualityChoice;
  spellNumber?: number | null;
}

function ballId(): string {
  return `ball-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * ENQUEUE ONE BALL. The sequence is allocated and the ball appended in the SAME transaction.
 *
 * Nothing is sent from here. A ball exists durably on the device the instant the button is released,
 * which is the only ordering that survives a phone with no signal — and the only one where a
 * force-quit two seconds later still has the ball.
 *
 * `newSessionUuid()` is reused as the uuid source rather than adding a second generator: it is
 * `device/ids.ts`'s plain uuid v4 with the secure-context fallback that a phone on a plain-http LAN
 * needs. A `newDeliveryUuid()` belongs next to it in that file — this lane may not edit it.
 */
export async function enqueueBall(input: BallInput): Promise<BallJob> {
  const device = await deviceId();
  const now = Date.now();
  return updateDoc((doc) => {
    const sequence = nextSequenceIn(doc, input.sessionId);
    const stamp: IdempotencyStamp = {
      delivery_uuid: `ball-${newSessionUuid()}`,
      device_id: device,
      recorded_at_device: new Date(now).toISOString(),
    };
    const body: DeliveryBody = {
      sequence,
      athlete_role: input.athleteRole,
      ball_source: input.ballSource,
      outcome_class: input.outcomeClass,
      // Always a human tag. See the header block: this is what makes a USABLE ball legal without a
      // detector, and it is simply true — a person tapped the button.
      outcome_source: input.outcomeSource,
      quality_state: input.quality.state,
      ...(input.quality.reason ? { quality_reason_code: input.quality.reason } : {}),
      ...(input.spellNumber ? { spell_number: input.spellNumber } : {}),
      events_json: { [IDEMPOTENCY_STAMP_KEY]: stamp },
    };
    const job: BallJob = {
      id: ballId(),
      session_id: input.sessionId,
      athlete_id: input.athleteId,
      sequence,
      state: 'queued',
      attempts: 0,
      created_at: now,
      updated_at: now,
      client_delivery_uuid: stamp.delivery_uuid,
      client_device_id: device,
      body,
      last_error: null,
      last_error_kind: null,
      last_error_detail: null,
      server_delivery_id: null,
      replay_proved: null,
    };
    return { doc: { ...doc, balls: [...doc.balls, job] }, out: job };
  });
}

async function patchBall(id: string, fn: (b: BallJob) => BallJob): Promise<void> {
  await updateDoc((doc) => ({
    doc: {
      ...doc,
      balls: doc.balls.map((b) => (b.id === id ? { ...fn(b), updated_at: Date.now() } : b)),
    },
    out: undefined,
  }));
}

/**
 * Record a refusal against a ball, choosing `held` vs `refused` HONESTLY.
 *
 * The same distinction `queue/queue.ts` draws, and the same function decides it. `held` means no
 * amount of retrying by this phone changes the answer — a stopped lab, an unmounted route — and the
 * drain stops touching the ball until a person taps "try again". Collapsing the two is how a
 * deliberate stop turns into an app that appears to be trying forever.
 */
function applyRefusal(b: BallJob, r: Refusal): BallJob {
  return {
    ...b,
    state: needsOperator(r) ? 'held' : 'refused',
    last_error: r.message,
    last_error_kind: r.kind,
    last_error_detail: {
      status: r.status,
      reason: r.reason,
      switch_name: r.switchName,
      missing: r.missing,
      note: r.note,
      request: r.request,
      // Only `unreachable` carries one; it is what a rehydrated transport failure is rebuilt from.
      transport_error: (r.body as { transport_error?: unknown } | null)?.transport_error ?? null,
    },
  };
}

/**
 * Rebuild a `Refusal` from what was stored, so a queued failure renders through the SAME view.
 *
 * Two renderers for one condition drift, and the one that drifts is always the offline path — which
 * here is the path that matters most.
 *
 * THE `unreachable` BRANCH IS NOT A SPECIAL CASE, IT IS A CORRECTION. `classify()` is written for
 * an HTTP response and is total over statuses; a request that never reached a server has status 0,
 * which falls through to `unknown_refusal` — "Unrecognised response" on screen, for the single most
 * ordinary thing that happens at a cricket ground. `unreachable()` is the constructor that condition
 * already has, so it is the one used.
 */
export function rehydrateRefusal(b: BallJob): Refusal {
  const d = (b.last_error_detail ?? {}) as Record<string, unknown>;
  const label = typeof d['request'] === 'string'
    ? d['request'] : `POST ${NS}/sessions/${b.session_id}/deliveries`;

  if (b.last_error_kind === 'unreachable') {
    const rebuilt = unreachable(label, (d['transport_error'] ?? 'no detail') as unknown, null);
    // The stored sentence wins where there is one: `recoverStaleBalls()` writes a more specific one
    // ("interrupted before the server answered") than the generic transport wording.
    return b.last_error ? { ...rebuilt, message: b.last_error } : rebuilt;
  }

  return classify(
    typeof d['status'] === 'number' ? d['status'] : 0,
    {
      message: b.last_error,
      reason: d['reason'] ?? null,
      switch_name: d['switch_name'] ?? null,
      missing: d['missing'] ?? [],
      note: d['note'] ?? null,
      ...(b.last_error_kind === 'lab_stopped' ? { error: 'practice_lab_unavailable' } : {}),
      ...(b.last_error_kind === 'not_mounted' ? { unmounted: true } : {}),
    },
    label,
  );
}

function stampOf(row: DeliveryRow): IdempotencyStamp | null {
  const events = row.events_json;
  if (!events || typeof events !== 'object') return null;
  const raw = (events as Record<string, unknown>)[IDEMPOTENCY_STAMP_KEY];
  if (!raw || typeof raw !== 'object') return null;
  const s = raw as Record<string, unknown>;
  return typeof s['delivery_uuid'] === 'string'
    ? {
      delivery_uuid: s['delivery_uuid'],
      device_id: typeof s['device_id'] === 'string' ? s['device_id'] : '',
      recorded_at_device: typeof s['recorded_at_device'] === 'string' ? s['recorded_at_device'] : '',
    }
    : null;
}

/**
 * RESOLVE A 409 BY LOOKING, NOT BY ASSUMING. See the header block.
 *
 * Returns without changing the ball's state when the lookup itself refuses — an ambiguity that
 * could not be settled stays visible as the 409 it was.
 */
async function resolveSequenceConflict(job: BallJob, conflict: Refusal): Promise<void> {
  const listed = await listDeliveries(job.session_id);
  if (isRefusal(listed)) {
    // The 409 stands, unresolved, with the server's own sentence. Nothing is claimed either way.
    await patchBall(job.id, (b) => applyRefusal(b, conflict));
    return;
  }
  await noteServerSequences(job.session_id, listed.value.deliveries.map((d) => d.sequence));
  const row = listed.value.deliveries.find((d) => d.sequence === job.sequence);
  const stamp = row ? stampOf(row) : null;

  if (row && stamp && stamp.delivery_uuid === job.client_delivery_uuid) {
    // OUR ball. It was written on an earlier attempt whose acknowledgement never arrived.
    await patchBall(job.id, (b) => ({
      ...b,
      state: 'recorded',
      server_delivery_id: row.id,
      replay_proved: true,
      last_error: null,
      last_error_kind: null,
      last_error_detail: null,
    }));
    return;
  }
  // Somebody else holds this sequence. NOT recorded, and not retryable by this phone.
  await patchBall(job.id, (b) => ({
    ...applyRefusal(b, conflict),
    state: 'collision',
  }));
}

async function sendBall(job: BallJob): Promise<void> {
  await patchBall(job.id, (b) => ({ ...b, state: 'sending', attempts: b.attempts + 1 }));
  // `job.body` was built at enqueue and is sent byte-identical on every attempt — including the
  // stamp. Rebuilding it here, and re-minting the uuid inside it, is the one change that would
  // break the replay proof while leaving every "it eventually succeeds" test green.
  // ── A THROW MUST NOT STRAND THE BALL (26 Aug 2026) ────────────────────────────────────────
  // This call was unguarded. The state is set to `sending` on the line above, so anything that
  // THREW here — rather than returning a Refusal — left the ball in `sending` permanently:
  // drainBalls() only picks up `queued` and `refused`, so it skipped that ball on every later pass.
  //
  // The founder recorded two balls, saw "Waiting on this phone: 2", and the server's own count
  // stayed 0 with nothing refused and nothing held. There was no error anywhere on the screen,
  // because the throw escaped the click handler's `void (async () => …)` as an unhandled rejection.
  //
  // A network fault becoming an invisible permanent stall is the same defect class as everything
  // else found today: the failure looks exactly like the success.
  let res: Awaited<ReturnType<typeof recordDelivery>>;
  try {
    res = await recordDelivery(job.session_id, job.body);
  } catch (e: unknown) {
    await patchBall(job.id, (b) => ({
      ...b,
      state: 'refused',                       // refused, NOT sending — so the next drain retries it
      last_error: `The request did not complete: ${String((e as Error)?.message ?? e)}. `
        + 'The ball is still on this phone and will be sent again.',
      last_error_kind: 'unreachable',
    }));
    return;
  }

  if (isRefusal(res)) {
    if (res.status === 409) {
      await resolveSequenceConflict(job, res);
      return;
    }
    await patchBall(job.id, (b) => applyRefusal(b, res));
    return;
  }
  await patchBall(job.id, (b) => ({
    ...b,
    state: 'recorded',
    server_delivery_id: res.value.delivery?.id ?? null,
    replay_proved: false,
    last_error: null,
    last_error_kind: null,
    last_error_detail: null,
  }));
}

let draining = false;

/**
 * Replay every runnable ball, OLDEST FIRST and SERIAL.
 *
 * Order matters here in a way it does not for uploads: sequence 7 arriving before sequence 6 is
 * harmless to the database but produces a block summary computed over a partial block. Serial also
 * keeps one phone on one bar of signal from fanning out fifty POSTs that all time out.
 *
 * Reentrancy-guarded: `online`, `visibilitychange` and a tap can all land in the same second.
 */
export async function drainBalls(): Promise<void> {
  if (draining) return;
  draining = true;
  try {
    for (const ball of await listBalls()) {
      // `held` and `collision` are skipped deliberately — neither changes because a phone asked
      // twice. They are resumed only by a person tapping.
      if (ball.state !== 'queued' && ball.state !== 'refused') continue;
      // Per-ball, so one ball that throws does not abort the balls behind it. Before this, a single
      // failure ended the loop and every later ball stayed queued with no reason recorded anywhere.
      try {
        await sendBall(ball);
      } catch (e: unknown) {
        await patchBall(ball.id, (b) => ({
          ...b,
          state: 'refused',
          last_error: `This ball could not be sent: ${String((e as Error)?.message ?? e)}`,
          last_error_kind: 'unreachable',
        }));
      }
    }
  } finally {
    draining = false;
  }
}

/**
 * Reset balls left mid-send by a crash, a force-quit or a tab close.
 *
 * A ball in `sending` with nobody working on it is stranded — `drainBalls()` skips it forever.
 * Resetting it is safe precisely because the replay is provable: either the POST never landed, or
 * it did and the 409 resolves against this ball's own stamp.
 */
export async function recoverStaleBalls(): Promise<number> {
  const stale = (await listBalls()).filter((b) => b.state === 'sending');
  for (const b of stale) {
    await patchBall(b.id, (cur) => ({
      ...cur,
      state: 'queued',
      last_error: 'Interrupted before the server answered — the app was closed or reloaded. It will '
        + 'be sent again, and the server will be asked whether this exact ball is already recorded.',
      last_error_kind: 'unreachable',
    }));
  }
  return stale.length;
}

/** A person says "try again" — including for a `held` or `collision` ball. */
export async function retryBall(id: string): Promise<void> {
  await patchBall(id, (b) => ({ ...b, state: 'queued', last_error: null, last_error_kind: null }));
  await drainBalls();
}

export interface BallCounts {
  /** The server has acknowledged these. */
  recorded: number;
  /** These exist ONLY on this phone. */
  waiting: number;
  sending: number;
  refused: number;
  held: number;
  collision: number;
  total: number;
}

/** Every state is named. There is no "other" bucket, and no merged "balls" number. */
export function countBalls(balls: readonly BallJob[]): BallCounts {
  const c: BallCounts = { recorded: 0, waiting: 0, sending: 0, refused: 0, held: 0, collision: 0, total: balls.length };
  for (const b of balls) {
    if (b.state === 'recorded') c.recorded += 1;
    else if (b.state === 'queued') c.waiting += 1;
    else if (b.state === 'sending') c.sending += 1;
    else if (b.state === 'refused') c.refused += 1;
    else if (b.state === 'held') c.held += 1;
    else c.collision += 1;
  }
  return c;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
 * 4 · THE SCREEN
 * ════════════════════════════════════════════════════════════════════════════════════════════ */

/** What each state SAYS, in words a coach can act on. `queued` never says "recorded". */
const STATE_LABEL: Record<BallState, string> = {
  queued: 'on this phone only',
  sending: 'sending…',
  recorded: 'recorded on the server',
  refused: 'refused — will try again',
  held: 'held — someone must fix the deployment',
  collision: 'that ball number is taken',
};

/** Reused from styles.css; `queued`/`uploading`/`done`/`failed`/`held` already have tags. */
const STATE_TAG: Record<BallState, string> = {
  queued: 'tag-queued',
  sending: 'tag-uploading',
  recorded: 'tag-done',
  refused: 'tag-failed',
  held: 'tag-held',
  collision: 'tag-failed',
};

/**
 * The selections, held OUTSIDE the render so fifty taps do not reset them.
 *
 * A net session is mostly the same settings ball after ball; making the coach re-pick "bowling,
 * live bowler" every time is how a screen that works in a demo fails at a ground.
 */
interface Controls {
  athleteRole: AthleteRole;
  ballSource: BallSource;
  outcomeClass: OutcomeClass;
  outcomeSource: HumanOutcomeSource;
  quality: QualityChoice;
}

const controls: Controls = {
  athleteRole: 'BOWLING',
  ballSource: 'LIVE_BOWLER',
  // The database default, and the honest one: the ball happened; nobody has judged it. A screen
  // that defaults to DOT invents a result for every ball the coach taps through quickly.
  outcomeClass: 'NOT_SCORED',
  outcomeSource: 'COACH_TAGGED',
  // The database default. "Not assessed" is a real answer; USABLE is a claim about the capture.
  quality: { state: 'PENDING', reason: null },
};

export async function deliveriesScreen(root: HTMLElement, rerender: () => void): Promise<void> {
  const st = getState();

  if (!st.athlete) {
    mount(root, section('Ball by ball',
      empty('No athlete selected. Choose one from the roster on the Coach screen first — an athlete '
        + 'id is only ever used here after the server handed it to us.')));
    return;
  }
  const athlete = st.athlete;

  if (!st.sessionId) {
    mount(root, section('Ball by ball',
      empty('No practice session is bound, and a delivery belongs to one. The session id is a path '
        + 'parameter the server issues — start or resume a session first; this screen will not '
        + 'invent one.'),
      el('p', { class: 'note' },
        'Balls already queued on this phone for other sessions are kept and will still be sent.')));
    return;
  }
  const sessionId = st.sessionId;

  // A ball stranded mid-send by a force-quit is put back in the queue before anything else happens.
  await recoverStaleBalls();

  const out = el('div');
  const counts = el('div');
  const blockPanel = el('div');
  const listPanel = el('div');
  const serverPanel = el('div');
  const lastAction = el('div');

  // ── The server's own view of this session, fetched once per screen open. It is what seeds the
  //    sequence high-water mark, so a second phone joining a session does not start at ball 1.
  let serverList: DeliveryListResult | null = null;
  let serverRefusal: Refusal | null = null;
  const listed = await listDeliveries(sessionId);
  if (isRefusal(listed)) serverRefusal = listed;
  else {
    serverList = listed.value;
    await noteServerSequences(sessionId, serverList.deliveries.map((d) => d.sequence));
  }

  const button = el('button', {
    class: 'btn btn-primary',
    // The most-tapped control in the product. Sized to be hit one-handed, outdoors, without looking.
    style: 'width:100%;min-height:120px;font-size:22px;line-height:1.3;margin:14px 0;',
  }, 'Record ball');

  async function refresh(): Promise<void> {
    const balls = await listBalls(sessionId);
    const c = countBalls(balls);
    const next = await nextSequence(sessionId);

    button.replaceChildren(document.createTextNode(
      `Record ball ${next}  ·  block ${blockOf(next)}, ball ${ballInBlock(next)} of ${BLOCK_SIZE}`));

    mount(counts,
      why('Two populations, never added together. The first is what the server holds; the second is '
        + 'what exists only on this phone. A ball is not recorded until the server says so.'),
      el('div', { class: 'counts' },
        field('Recorded on the server', c.recorded),
        // `waiting` and `sending` used to be ADDED together here. That merge is what hid the
        // defect: a ball stranded mid-send counted as one waiting to be sent, so a permanent stall
        // and a normal queue rendered the identical number. Two states, two numbers.
        field('Waiting on this phone', c.waiting),
        field('Sending now', c.sending),
        field('Refused (will retry)', c.refused),
        field('Held — needs an operator', c.held),
        field('Ball-number conflicts', c.collision)),
      serverList && el('p', { class: 'small' },
        `The server's own count for this session right now: ${serverList.deliveries.length}.`));

    mount(blockPanel, blockStrip(balls, next));
    mount(listPanel, ballList(balls, () => void refresh()));
  }

  button.addEventListener('click', () => {
    // Disabled for the duration of the write, then re-enabled. A double-tap cannot double-count
    // anyway — allocation and append share one transaction — but a coach deserves the feedback.
    button.setAttribute('disabled', '');
    void (async () => {
      try {
        const job = await enqueueBall({
          sessionId,
          athleteId: athlete.athleteId,
          athleteRole: controls.athleteRole,
          ballSource: controls.ballSource,
          outcomeClass: controls.outcomeClass,
          outcomeSource: controls.outcomeSource,
          quality: controls.quality,
        });
        mount(lastAction, el('p', { class: 'ok' },
          `Ball ${job.sequence} is saved on this phone. It is not recorded until the server says so.`));
        await refresh();
        await drainBalls();
        await refresh();
      } finally {
        button.removeAttribute('disabled');
      }
    })();
  });

  out.append(section('Ball by ball',
    el('p', { class: 'note' },
      `Session ${sessionId} · athlete ${athlete.label}. Blocks are ${BLOCK_SIZE} balls, the same `
      + 'block the coaching lane summarises on.'),
    button,
    lastAction,
    blockPanel,
    counts,
  ));

  out.append(section('What this ball says',
    el('p', { class: 'note' },
      'These stay put between balls. Every value below is the backend’s own vocabulary — this '
      + 'app has no words of its own for a ball.'),
    chips('The athlete is', ATHLETE_ROLES, () => controls.athleteRole, (v) => { controls.athleteRole = v; }),
    chips('Ball came from', BALL_SOURCES, () => controls.ballSource, (v) => { controls.ballSource = v; }),
    chips('Outcome', OUTCOME_CLASSES, () => controls.outcomeClass, (v) => { controls.outcomeClass = v; }),
    el('p', { class: 'small' },
      'NOT_SCORED is the default and it is the honest one: the ball happened and nobody has judged '
      + 'it yet. Nothing here fills that in for you.'),
    chips('Tagged by', HUMAN_OUTCOME_SOURCES, () => controls.outcomeSource, (v) => { controls.outcomeSource = v; }),
    el('p', { class: 'small' },
      'AI_INFERRED exists in the server’s vocabulary and is not offered: no model runs on this '
      + 'phone, so a ball tagged that way from here would have no author.'),
    qualityControl(),
  ));

  out.append(section('Balls in this session', listPanel));

  // ── The server's side, including its refusal if it had one.
  mount(serverPanel,
    serverRefusal
      ? refusalView(serverRefusal, { onRetry: rerender, onSignIn: rerender })
      : el('div', {},
        el('p', { class: 'note' },
          'Read back from the server, not from this phone’s copy. `capture` below is the '
          + 'OBSERVED capture condition of the session — it is read from the session row, and '
          + 'nothing this screen sends contributes to it.'),
        serverList && field('Balls the server holds', serverList.deliveries.length),
        serverList && el('details', {},
          el('summary', {}, 'Capture conditions and disclaimer'),
          el('pre', {}, JSON.stringify(serverList.capture, null, 2)),
          el('p', { class: 'small' }, serverList.disclaimer)),
      ));
  out.append(section('The server’s view of this session', serverPanel));

  await refresh();
  mount(root, out);
}

/* ── the six-ball block strip ─────────────────────────────────────────────────────────────────── */

/**
 * The current block, drawn as six slots, plus the blocks behind it as one line each.
 *
 * The slots show STATE, not just presence: a queued ball and a recorded ball look different, because
 * "six balls done" from a phone holding four unsent ones is not six balls done.
 */
function blockStrip(balls: readonly BallJob[], nextSeq: number): HTMLElement {
  const currentBlock = blockOf(nextSeq);
  const inBlock = (n: number): BallJob | undefined => balls.find((b) => b.sequence === n);
  const start = (currentBlock - 1) * BLOCK_SIZE + 1;

  const slots = el('div', { class: 'row', style: 'gap:6px;margin:10px 0;' });
  for (let n = start; n < start + BLOCK_SIZE; n++) {
    const b = inBlock(n);
    slots.append(el('span', {
      class: b ? `tag ${STATE_TAG[b.state]}` : 'tag',
      style: 'min-width:44px;text-align:center;padding:8px 6px;font-size:13px;',
      title: b ? `Ball ${n}: ${STATE_LABEL[b.state]}` : `Ball ${n}: not recorded yet`,
    }, String(n)));
  }

  const done = Array.from({ length: currentBlock - 1 }, (_, i) => i + 1).map((blk) => {
    const inThis = balls.filter((b) => blockOf(b.sequence) === blk);
    const rec = inThis.filter((b) => b.state === 'recorded').length;
    return el('p', { class: 'small' },
      `Block ${blk}: ${rec} of ${inThis.length} balls recorded on the server`);
  });

  return el('div', {},
    el('p', { class: 'small' }, `Block ${currentBlock} — balls ${start} to ${start + BLOCK_SIZE - 1}`),
    slots,
    ...done.slice(-4),
    done.length > 4 && el('p', { class: 'small' }, `…and ${done.length - 4} earlier blocks.`),
  );
}

/* ── the ball list ────────────────────────────────────────────────────────────────────────────── */

function ballList(balls: readonly BallJob[], onChange: () => void): HTMLElement {
  if (!balls.length) {
    return empty('No balls recorded in this session yet. The button above writes one to this phone '
      + 'first, then sends it.');
  }
  const newestFirst = [...balls].reverse();
  const head = newestFirst.slice(0, 12);
  const rest = newestFirst.slice(12);

  const row = (b: BallJob): HTMLElement => el('div', { class: 'row-item' },
    el('div', { class: 'field' },
      el('span', { class: 'field-label' },
        `Ball ${b.sequence} · block ${blockOf(b.sequence)}, ${ballInBlock(b.sequence)} of ${BLOCK_SIZE}`),
      el('span', { class: `tag ${STATE_TAG[b.state]}` }, STATE_LABEL[b.state])),
    field('Outcome', `${b.body.outcome_class} (${b.body.outcome_source})`),
    field('Ball source', b.body.ball_source),
    field('Athlete role', b.body.athlete_role),
    field('Capture quality', b.body.quality_reason_code
      ? `${b.body.quality_state} — ${b.body.quality_reason_code}`
      : b.body.quality_state),
    b.state === 'recorded' && field('Server row', b.server_delivery_id ?? '—'),
    b.replay_proved === true && el('p', { class: 'ok' },
      'This ball had already reached the server on an earlier attempt — the reply was lost, not the '
      + 'ball. Its own stamp was found on the server’s row, so nothing was written twice.'),
    b.state === 'collision' && el('p', { class: 'warn' },
      `Ball number ${b.sequence} in this session belongs to a delivery that is not this one. This `
      + 'ball has NOT been recorded. Nothing on this phone can settle that — a person has to decide '
      + 'whether it is a duplicate or needs a different number.'),
    b.state === 'recorded' && b.server_delivery_id && observationsPanel(b.server_delivery_id),
    b.last_error && b.last_error_kind
      && refusalView(rehydrateRefusal(b), { onRetry: () => { void retryBall(b.id).then(onChange); } }),
  );

  return el('div', { class: 'list' },
    ...head.map(row),
    rest.length > 0 && el('details', {},
      el('summary', {}, `${rest.length} earlier balls`),
      el('div', { class: 'list' }, ...rest.map(row))),
  );
}

/**
 * Metrics for one recorded ball, fetched only when opened.
 *
 * MEASURED and REFUSED are shown together, which is the backend's explicit intent: a refusal tells a
 * coach WHY a number is absent, and dropping it turns "we could not measure that" into "nothing
 * happened". Fetched on open rather than on list render because a hundred-ball session would
 * otherwise fire a hundred requests at a phone on one bar.
 */
function observationsPanel(deliveryId: string): HTMLElement {
  const body = el('div');
  let loaded = false;
  const details = el('details', {},
    el('summary', {}, 'Metrics on this ball'),
    body);
  details.addEventListener('toggle', () => {
    if (!details.open || loaded) return;
    loaded = true;
    mount(body, el('p', { class: 'loading' }, 'Asking the server…'));
    void (async () => {
      const r = await listDeliveryObservations(deliveryId);
      if (isRefusal(r)) { mount(body, refusalView(r)); return; }
      const v = r.value;
      if (!v.observations.length) {
        mount(body, empty('No metric has been offered for this ball. This phone measures nothing — '
          + 'metrics arrive from the analysis pipeline, not from the person tapping.'));
        return;
      }
      mount(body,
        field('Measured', v.measured_count),
        field('Refused', v.refused_count),
        ...v.observations.map((o) => el('div', { class: 'row-item' },
          field('Metric', o.metric_id),
          field('State', o.observation_state),
          o.observation_state === 'REFUSED'
            ? field('Why not', o.refusal_reason_code ?? '—')
            : field('Measured as', `${o.ai_value_num ?? '—'} ${o.unit ?? ''}`.trim()),
          field('Measurement class', o.measurement_class ?? '—'),
          field('Review state', o.review_state ?? '—'))),
        v._note && el('p', { class: 'small' }, v._note),
        el('p', { class: 'small' }, v.disclaimer));
    })();
  });
  return details;
}

/* ── controls ─────────────────────────────────────────────────────────────────────────────────── */

/** A row of single-tap chips over a closed vocabulary. There is no free-text alternative. */
function chips<T extends string>(
  label: string,
  values: readonly T[],
  get: () => T,
  set: (v: T) => void,
): HTMLElement {
  const row = el('div', { class: 'row' });
  const paint = (): void => {
    row.replaceChildren();
    for (const v of values) {
      row.append(el('button', {
        class: `btn${v === get() ? ' btn-primary' : ''}`,
        style: 'min-height:44px;',
        onclick: () => { set(v); paint(); },
      }, v));
    }
  };
  paint();
  return el('div', {}, el('span', { class: 'label' }, label), row);
}

/**
 * THE QUALITY CONTROL, SHAPED SO THE ILLEGAL PAIRING CANNOT BE TAPPED.
 *
 * PENDING and USABLE are one tap each. DEGRADED and UNUSABLE are chosen BY choosing the reason —
 * there is no "DEGRADED" button that leaves the reason blank, because the database refuses that
 * combination and "bad without a reason is not a finding".
 */
function qualityControl(): HTMLElement {
  const row = el('div', { class: 'row' });
  const reasons = el('div');
  const current = el('div');

  const paint = (): void => {
    mount(current, field('This ball’s capture quality',
      controls.quality.reason ? `${controls.quality.state} — ${controls.quality.reason}` : controls.quality.state));
    row.replaceChildren();
    for (const s of ['PENDING', 'USABLE'] as const) {
      row.append(el('button', {
        class: `btn${controls.quality.state === s ? ' btn-primary' : ''}`,
        style: 'min-height:44px;',
        onclick: () => { controls.quality = { state: s, reason: null }; paint(); },
      }, s));
    }
    for (const s of ['DEGRADED', 'UNUSABLE'] as const) {
      row.append(el('button', {
        class: `btn${controls.quality.state === s ? ' btn-primary' : ''}`,
        style: 'min-height:44px;',
        onclick: () => { paintReasons(s); },
      }, `${s}…`));
    }
  };

  const paintReasons = (state: 'DEGRADED' | 'UNUSABLE'): void => {
    mount(reasons,
      el('p', { class: 'small' },
        `A ${state} ball must say why — the server refuses one that does not. Pick the reason and `
        + 'the state is set with it.'),
      el('div', { class: 'row' }, ...QUALITY_REASON_CODES.map((code) => el('button', {
        class: 'btn',
        style: 'min-height:44px;',
        onclick: () => { controls.quality = { state, reason: code }; mount(reasons); paint(); },
      }, code))),
      el('button', { class: 'btn btn-quiet', onclick: () => { mount(reasons); } }, 'Cancel'));
  };

  paint();
  return el('div', {}, el('span', { class: 'label' }, 'Capture quality'), row, reasons, current);
}
